import {
  ArrowDownIcon,
  ArrowUpIcon,
  Bin,
  EmptyStateButton,
  Info,
  Input,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import styles from '../CreateOptionGroupModal.module.scss';
import WarningBanner from '../../WarningBanner/WarningBanner';
import { FEATURE_FLAGS, GLOBAL_ITEM_UPDATE_NOTE, ItemType, OptionGroupType } from '../../../constants';
import { useAuthWrapper } from '@weareopenr/authkit';
import { ProductRangeSelector } from '../../ProductRangeSelector/ProductRangeSelector';
import OptionGroupOption from '../OptionGroupOption/OptionGroupOption';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useRanges } from '../../../hooks/useRanges/useRanges';
import { IOptionGroupValues } from '../types';
import { useEffect, useMemo, useState } from 'react';
import type { IMenuItem, IOptionGroup, TMenuItemType } from 'src/types/menu.types';
import { getAllSelectableItems } from '../utilities/getAllSelectableItems/getAllSelectableItems';
import { useItems } from '../../../hooks/useItems';
import { pluralize } from '../../../helpers/pluralize/pluralize';
import type { IOption } from '@weareopenr/component-library';

const newGroupOption = {
  optionItemId: '',
  parentItemId: '',
  name: '',
  posId: '',
  valueWithTax: null,
  useDefaultPrice: false,
  inheritPosId: true,
};

interface IOptionGroupDetailsProps {
  optionGroup?: IOptionGroup;
  useFormReturn: UseFormReturn<IOptionGroupValues>;
  optionGroupType: OptionGroupType;
}

export const OptionGroupDetails = ({ optionGroup, useFormReturn, optionGroupType }: IOptionGroupDetailsProps) => {
  const isEdit = !!optionGroup;
  // The group PLU is only returned by the standalone option group endpoint. When editing from a context
  // that does not supply it (e.g. the item page) the field is hidden so an unchanged value is never overwritten.
  const canEditPlu = !isEdit || optionGroup?.plu !== undefined;
  const { featureFlags } = useAuthWrapper();
  const independentOptionGroups = featureFlags?.includes(FEATURE_FLAGS.INDEPENDENT_OPTION_GROUPS) ?? false;
  const { data: ranges } = useRanges({
    enabled: !isEdit,
  });
  // if a user is editing an option group, they are locked to the ranges already selected
  const availableRanges = (isEdit ? optionGroup?.ranges : ranges) || [];

  const {
    register,
    formState: { errors },
    trigger,
    setValue,
    clearErrors,
    watch,
    control,
  } = useFormReturn;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'modifiers',
    rules: {
      minLength: 1,
      required: true,
    },
  });

  const isMissingModifiers = errors.modifiers?.root?.type === 'required';
  const isModifiersNotMatchingRange = errors.modifiers?.some((error) => error?.type === 'nonMatchingModifierRange');

  const watchRanges = watch('ranges');
  const watchModifiers = watch('modifiers');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectableItems, setSelectableItems] = useState<IMenuItem[]>([]);

  const itemTypes: TMenuItemType[] =
    optionGroupType === OptionGroupType.BUNDLE
      ? [ItemType.STANDARD, ItemType.NON_FOOD]
      : [ItemType.STANDARD, ItemType.MODIFIER, ItemType.NON_FOOD];

  const { data, hasNextPage, fetchNextPage } = useItems(searchTerm, undefined, itemTypes, undefined, watchRanges);

  const loadMoreItems = () => {
    if (hasNextPage) fetchNextPage();
  };

  const addNewGroupOption = (): void => {
    append(newGroupOption);
    trigger(['min_choices', 'max_choices']);
  };

  const removeGroupOption = (index: number) => {
    remove(index);
    trigger(['min_choices', 'max_choices']);
  };

  useEffect(() => {
    // Always replace selectableItems with the latest data for the current range/search
    const newItems: IMenuItem[] = data?.pages?.flat() || [];
    setSelectableItems(newItems);
  }, [data, watchRanges]);

  const formattedOptionGroupOptions = useMemo(() => {
    return getAllSelectableItems(selectableItems, optionGroup);
  }, [selectableItems, optionGroup]);

  const groupContentNameSingular = optionGroupType === OptionGroupType.BUNDLE ? 'Product' : 'Modifier';
  const groupContentNamePlural = pluralize(groupContentNameSingular, 2);
  const groupType = optionGroupType === OptionGroupType.BUNDLE ? 'Bundle' : 'Modifier';

  return (
    <>
      <Typography size="xxlarge" weight="semi-bold" className={styles['heading-text']}>
        {`${isEdit ? 'Edit' : 'Create'} ${groupType} Group`}
      </Typography>
      <Typography size="medium" weight="regular" className={styles['sub-heading-text']}>
        {`${isEdit ? 'Update' : 'Add'} ${groupType} group details`}
      </Typography>
      <WarningBanner text={GLOBAL_ITEM_UPDATE_NOTE} />
      <section className={styles['section']}>
        <Typography size="xlarge" weight="semi-bold" className={styles['heading-text']}>
          {`${groupType} group`}
        </Typography>
        <div className={styles['input-wrapper']}>
          <Input
            type="text"
            label="Name"
            hasMargin
            register={register('name', { required: 'Please enter a name' })}
            error={errors?.name?.message}
          />
        </div>
        <div className={styles['input-wrapper']}>
          <Input type="text" label="Consumer Name" hasMargin register={register('consumer_name')} />
        </div>
        {canEditPlu && (
          <div className={styles['input-wrapper']}>
            <Input type="text" label="Group POS ID" hasMargin register={register('plu')} />
          </div>
        )}
        {optionGroupType === OptionGroupType.MODIFIER && (
          <div className={styles['input-wrapper']}>
            <Input
              type="text"
              label="Description"
              hasMargin
              register={register('description', { required: 'Please enter a description' })}
              rows={2}
              error={errors?.description?.message}
            />
          </div>
        )}
        <div className={styles['tooltip-container']}>
          <Typography>Range of choices</Typography>
          <Tooltip text="e.g. Min 1 & Max 3 means at least one choice is required, plus ability to choose up to 3">
            <Info className={styles['info-icon']} />
          </Tooltip>
        </div>
        <div className={styles['range-container']}>
          <Input
            label="Min"
            hasMargin
            type="number"
            register={register('min_choices', {
              required: true,
              valueAsNumber: true,
              validate: {
                isInRange: (v) => (v >= 0 && v <= 99) || 'Min should be between 0 and 99',
                isUnderMaxValue: (v, formValues) => v <= formValues.max_choices || "Min can't be greater than max",
                isUnderSumOfOptionMaxes: (v, formValues) => {
                  const optionMaxes = formValues.modifiers.map((m) => m.max).filter((max) => max != null && max > 0);
                  if (optionMaxes.length === 0) return true;
                  const sum = optionMaxes.reduce((acc, max) => acc + max, 0);
                  return v <= sum || `Group min cannot be greater than the sum of option maxes (${sum})`;
                },
                isAboveSumOfOptionMins: (v, formValues) => {
                  const optionMins = formValues.modifiers.map((m) => m.min).filter((min) => min != null && min > 0);
                  if (optionMins.length === 0) return true;
                  const sum = optionMins.reduce((acc, min) => acc + min, 0);
                  return v >= sum || `Group min can't be less than the cumulative option min (${sum})`;
                },
              },
              onChange: () => {
                trigger(['min_choices', 'max_choices']);
              },
            })}
            error={errors?.min_choices?.message}
          />
          <div className={styles['separator-container']}>
            <span className={styles['separator']} />
          </div>
          <Input
            label="Max"
            type="number"
            register={register('max_choices', {
              required: true,
              valueAsNumber: true,
              validate: {
                isInRange: (v) => (v >= 1 && v <= 99) || 'Max should be between 1 and 99',
                isAboveMin: (v, formValues) => v >= formValues.min_choices || "Max can't be less than min",
                isAboveHighestOptionMax: (v, formValues) => {
                  const optionMaxes = formValues.modifiers.map((m) => m.max).filter((max) => max != null && max > 0);
                  if (optionMaxes.length === 0) return true;
                  const highest = Math.max(...optionMaxes);
                  return v >= highest || `Group max cannot be less than the highest option max (${highest})`;
                },
                isAboveCumulativeOptionMax: (v, formValues) => {
                  const optionMaxes = formValues.modifiers.map((m) => m.max).filter((max) => max != null && max > 0);
                  if (optionMaxes.length === 0) return true;
                  const sum = optionMaxes.reduce((acc, max) => acc + max, 0);
                  return v <= sum || `Group max cannot be greater than the cumulative option max (${sum})`;
                },
              },
              onChange: () => {
                trigger(['min_choices', 'max_choices']);
              },
            })}
            error={errors?.max_choices?.message}
          />
        </div>
        {(independentOptionGroups || optionGroupType === OptionGroupType.MODIFIER) && (
          <ProductRangeSelector
            ranges={availableRanges}
            register={register}
            setValue={setValue}
            errors={errors}
            watch={watch}
            clearErrors={clearErrors}
            trigger={trigger}
            productType={ItemType.STANDARD}
            control={control}
            isDisabled={isEdit}
          />
        )}
      </section>

      <section className={styles['section']}>
        <Typography size="xlarge" weight="semi-bold" className={styles['heading-text']}>
          {groupContentNamePlural}
        </Typography>
        {isModifiersNotMatchingRange && (
          <div className={styles['error-message']}>
            Modifier does not belong to the range selected <Info className={styles['info-icon']} />
          </div>
        )}
        {fields.length > 0 && (
          <div className={styles['option-group-options-grid']} data-testid="field-group-headings">
            <Typography weight="semi-bold">Product Name</Typography>
            <Typography className={styles['pos-id-heading']} weight="semi-bold">
              Inherit POS ID
            </Typography>
            <Typography weight="semi-bold">Override POS ID</Typography>
            <Typography weight="semi-bold" className={styles['quantities-heading-container']}>
              Quantities
            </Typography>
            <div />
            {fields.map((field, index) => {
              const options: IOption[] = [
                {
                  id: 0,
                  name: 'Move up',
                  isDisabled: index === 0,
                  onOptionClick: () => move(index, index - 1),
                  persistMenuOnClick: true,
                  icon: ArrowUpIcon,
                },
                {
                  id: 1,
                  name: 'Move down',
                  isDisabled: index + 1 === fields.length,
                  onOptionClick: () => move(index, index + 1),
                  persistMenuOnClick: true,
                  icon: ArrowDownIcon,
                },
                { id: 'delete-divider', isDivider: true },
                {
                  id: 2,
                  name: 'Delete',
                  onOptionClick: () => removeGroupOption(index),
                  icon: Bin,
                  hoverColour: 'tomato',
                },
              ];

              const selectedIds = new Set(
                watchModifiers.flatMap((modifier, i) =>
                  i !== index && modifier.optionItemId ? [modifier.optionItemId] : [],
                ),
              );
              const availableModifiers = formattedOptionGroupOptions.filter(
                (item) => !selectedIds.has(item.parentItemId || item.optionItemId),
              );

              return (
                <OptionGroupOption
                  key={field.id}
                  index={index}
                  modifiers={availableModifiers}
                  ellipsisMenuOptions={options}
                  useFormReturn={useFormReturn}
                  onLoadMore={loadMoreItems}
                  setSearchTerm={setSearchTerm}
                  groupContentNameSingular={groupContentNameSingular}
                />
              );
            })}
          </div>
        )}
        {isMissingModifiers && (
          <div className={styles['error-message']}>
            {groupContentNameSingular} needs to be added <Info className={styles['info-icon']} />
          </div>
        )}
        <div className={styles['add-modifier-container']}>
          <EmptyStateButton
            label={`+ Add ${groupContentNameSingular}`}
            onClick={() => {
              addNewGroupOption();
            }}
          />
        </div>
      </section>
    </>
  );
};
